import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';

import garantiasApi from '../../../api/garantiasApi';
import productsApi from '../../../api/productsApi';
import { applyServerErrors, getErrorMessage } from '../../../utils/errorHandler';
import { GARANTIA_RESPONSE_DAYS_OPTIONS } from '../../../utils/garantiaObservations';
import GarantiaImagesManager from './GarantiaImagesManager';

const schema = yup.object({
  document_number: yup.string().trim().required('Ingresa el N.° de boleta, factura o nota de venta'),
  date: yup.string().required('Selecciona la fecha de venta'),
  intake_date: yup.string().required('Selecciona la fecha de ingreso'),
  response_days: yup.number().oneOf(GARANTIA_RESPONSE_DAYS_OPTIONS).required('Selecciona el tiempo de respuesta'),
  informe: yup.string().trim().required('Describe la falla reportada'),
  observations: yup.string().trim().nullable(),
});

// Cada linea es un producto del catalogo ({ product, serie }) o un producto "libre" de una
// venta express ({ description, serie }), que puede no tener serie.
const itemName = (item) => (item.product ? item.product.name : `${item.description} (producto libre)`);
const isSameItem = (a, b) =>
  (a.product?.id ?? null) === (b.product?.id ?? null) &&
  (a.description || '') === (b.description || '') &&
  (a.serie || '') === (b.serie || '');

// Fecha local de hoy en formato YYYY-MM-DD (el que usa el <input type="date">).
const todayIso = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const emptyValues = {
  document_number: '',
  date: '',
  intake_date: '',
  response_days: 15,
  informe: '',
  observations: '',
};

// Alta/edicion de una garantia: numero de comprobante, producto(s) con su serie, informe de
// la falla, observaciones del estado en que se recibio y tiempo de respuesta. Las fotos de
// evidencia solo se pueden agregar una vez que la garantia ya existe (tienen su propio
// endpoint, ver GarantiaImagesManager), por eso tras el primer guardado el dialogo se queda
// abierto en modo edicion en vez de cerrarse.
export default function GarantiaFormDialog({ open, onClose, onSaved, garantia = null }) {
  const { enqueueSnackbar } = useSnackbar();

  const [savedGarantia, setSavedGarantia] = useState(null);
  const [items, setItems] = useState([]);

  const [productOptions, setProductOptions] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [serieInput, setSerieInput] = useState('');
  const serieInputRef = useRef(null);
  const scanInputRef = useRef(null);

  // Lector de codigo de barras: escribe el codigo y manda Enter, igual que si se tipeara.
  const [scanInput, setScanInput] = useState('');
  const [scanning, setScanning] = useState(false);
  const [saleDocument, setSaleDocument] = useState(null);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: emptyValues,
  });

  const isEditing = Boolean(savedGarantia);

  useEffect(() => {
    if (open) {
      reset({
        document_number: garantia?.document_number || '',
        // Fecha de venta: se completa sola al escanear el comprobante (ver applySaleDocument).
        date: garantia?.date || '',
        // Fecha de ingreso: por defecto hoy al registrar una garantia nueva.
        intake_date: garantia?.intake_date || todayIso(),
        response_days: garantia?.response_days || 15,
        informe: garantia?.informe || '',
        observations: garantia?.observations || '',
      });
      setItems(
        garantia?.items?.map((item) => ({
          product: item.product || null,
          description: item.description || '',
          serie: item.serie || '',
        })) || []
      );
      setSavedGarantia(garantia || null);
      setSelectedProduct(null);
      setProductSearch('');
      setSerieInput('');
      setScanInput('');
      setSaleDocument(null);
    }
    // Los valores iniciales solo se leen al abrir el modal, no en cada render del padre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, garantia, reset]);

  // Busca productos conforme el usuario escribe, con un pequeno debounce.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const timeout = setTimeout(async () => {
      setSearchingProducts(true);
      try {
        const { data } = await productsApi.getProducts({ search: productSearch, per_page: 10, is_active: 1 });
        setProductOptions(data.data);
      } catch (error) {
        // Busqueda secundaria: si falla no se interrumpe el armado de la garantia.
      } finally {
        setSearchingProducts(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [open, productSearch]);

  const addLine = (line) => {
    setItems((prev) => (prev.some((item) => isSameItem(item, line)) ? prev : [...prev, line]));
  };

  const handleAddItem = () => {
    if (!selectedProduct || !serieInput.trim()) {
      return;
    }
    addLine({ product: selectedProduct, description: '', serie: serieInput.trim() });
    setSelectedProduct(null);
    setProductSearch('');
    setSerieInput('');
  };

  // Producto del catalogo sin serie conocida: se pasa al selector manual para escribirle la serie.
  const pickProductForSerie = (product) => {
    setProductOptions((prev) => (prev.some((option) => option.id === product.id) ? prev : [product, ...prev]));
    setSelectedProduct(product);
    setTimeout(() => serieInputRef.current?.focus(), 0);
  };

  const applySaleDocument = (document) => {
    setValue('document_number', document.document_number, { shouldValidate: true });
    if (document.sale_date) {
      setValue('date', document.sale_date, { shouldValidate: true });
    }
    setSaleDocument(document);
  };

  const handleScan = async () => {
    const code = scanInput.trim();
    if (!code) {
      return;
    }

    setScanning(true);
    try {
      const { data } = await garantiasApi.lookupCode(code);
      const result = data.data;

      if (result.type === 'document') {
        applySaleDocument(result);
        enqueueSnackbar(`Venta ${result.document_number} encontrada: elige el producto en garantía.`, { variant: 'success' });
      } else if (result.type === 'serial') {
        addLine({ product: result.product, description: '', serie: result.serie });
        if (result.document && !getValues('document_number')) {
          applySaleDocument(result.document);
        }
        enqueueSnackbar(`Serie ${result.serie} agregada.`, { variant: 'success' });
      } else if (result.type === 'product') {
        pickProductForSerie(result.product);
        enqueueSnackbar(`${result.product.name}: ingresa o escanea su N.° de serie.`, { variant: 'info' });
      }
      setScanInput('');
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo reconocer el código.'), { variant: 'warning' });
    } finally {
      setScanning(false);
    }
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleImagesChanged = async () => {
    try {
      const { data } = await garantiasApi.getGarantia(savedGarantia.id);
      setSavedGarantia(data.data);
      onSaved();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo actualizar la lista de imágenes.'), { variant: 'error' });
    }
  };

  const onSubmit = async (data) => {
    if (items.length === 0) {
      enqueueSnackbar('Agrega al menos un producto.', { variant: 'warning' });
      return;
    }

    const payload = {
      document_number: data.document_number,
      date: data.date,
      intake_date: data.intake_date,
      response_days: Number(data.response_days),
      informe: data.informe,
      observations: data.observations || undefined,
      items: items.map((item) =>
        item.product
          ? { product_id: item.product.id, serie: item.serie }
          : { description: item.description, serie: item.serie || undefined }
      ),
    };

    try {
      const { data: response } = isEditing
        ? await garantiasApi.updateGarantia(savedGarantia.id, payload)
        : await garantiasApi.createGarantia(payload);
      setSavedGarantia(response.data);
      enqueueSnackbar(
        isEditing ? 'Garantía actualizada correctamente.' : 'Garantía registrada correctamente.',
        { variant: 'success' }
      );
      onSaved();
    } catch (error) {
      const handled = applyServerErrors(error, setError);
      if (!handled) {
        enqueueSnackbar(getErrorMessage(error, 'No se pudo guardar la garantía.'), { variant: 'error' });
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      // El foco va al campo de escaneo apenas termina de abrirse, para usar el lector sin hacer clic.
      slotProps={{ transition: { onEntered: () => scanInputRef.current?.focus() } }}
    >
      <DialogTitle>{isEditing ? 'Editar garantía' : 'Nueva garantía'}</DialogTitle>

      <DialogContent dividers>
        <Stack component="form" id="garantia-form" spacing={3} onSubmit={handleSubmit(onSubmit)} noValidate>
          <TextField
            fullWidth
            inputRef={scanInputRef}
            label="Escanear código"
            placeholder="Boleta, factura o nota de venta · código de barras del producto · N.° de serie"
            value={scanInput}
            onChange={(e) => setScanInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleScan();
              }
            }}
            disabled={scanning}
            helperText="Escanea con el lector (o escribe y presiona Enter) para reconocer la venta o el producto."
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <QrCodeScannerIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />

          <TextField
            fullWidth
            label="N.° de boleta, factura o nota de venta"
            placeholder="Ej. B001-000012, F001-000003 o NV-00000005"
            error={Boolean(errors.document_number)}
            helperText={errors.document_number?.message}
            slotProps={{ inputLabel: { shrink: true } }}
            {...register('document_number')}
          />

          {saleDocument && (
            <Alert severity="info" icon={false}>
              <Typography variant="subtitle2" gutterBottom>
                Productos de la venta {saleDocument.document_number}
                {saleDocument.sale_date ? ` (${saleDocument.sale_date})` : ''}
              </Typography>
              <Stack spacing={1}>
                {saleDocument.items.map((saleItem, index) => {
                  const name = saleItem.product ? saleItem.product.name : `${saleItem.description} (producto libre)`;
                  const base = { product: saleItem.product, description: saleItem.description || '' };

                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <Stack sx={{ alignItems: { sm: 'center' } }} key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {name} × {saleItem.quantity}
                      </Typography>
                      {saleItem.serials.length > 0 ? (
                        saleItem.serials.map((serie) => (
                          <Button
                            key={serie}
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => addLine({ ...base, serie })}
                          >
                            Serie {serie}
                          </Button>
                        ))
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() =>
                            saleItem.product ? pickProductForSerie(saleItem.product) : addLine({ ...base, serie: '' })
                          }
                        >
                          Agregar
                        </Button>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </Alert>
          )}

          <Stack spacing={1.5}>
            <Typography variant="subtitle1">Producto(s) con su serie</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Autocomplete
                sx={{ flex: 2 }}
                options={productOptions}
                value={selectedProduct}
                loading={searchingProducts}
                getOptionLabel={(option) => (option ? `${option.name} (SKU ${option.sku})` : '')}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(event, value) => setSelectedProduct(value)}
                onInputChange={(event, value) => setProductSearch(value)}
                renderInput={(params) => <TextField {...params} label="Producto" placeholder="Nombre o SKU" />}
              />
              <TextField
                sx={{ flex: 1 }}
                label="N.° de serie"
                inputRef={serieInputRef}
                value={serieInput}
                onChange={(e) => setSerieInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddItem();
                  }
                }}
              />
              <Button
                variant="outlined"
                onClick={handleAddItem}
                disabled={!selectedProduct || !serieInput.trim()}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Agregar
              </Button>
            </Stack>

            {items.length === 0 ? (
              <Alert severity="info">Aún no agregaste productos.</Alert>
            ) : (
              <List disablePadding>
                {items.map((item, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <ListItem
                    key={`${item.product?.id ?? item.description}-${index}`}
                    divider
                    disableGutters
                    secondaryAction={
                      <IconButton size="small" onClick={() => removeItem(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={itemName(item)} secondary={item.serie ? `Serie: ${item.serie}` : 'Sin serie'} />
                  </ListItem>
                ))}
              </List>
            )}
          </Stack>

          <Divider />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                type="date"
                label="Fecha de venta"
                slotProps={{ inputLabel: { shrink: true } }}
                error={Boolean(errors.date)}
                helperText={errors.date?.message || 'Fecha del comprobante.'}
                {...register('date')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                type="date"
                label="Fecha de ingreso"
                slotProps={{ inputLabel: { shrink: true } }}
                error={Boolean(errors.intake_date)}
                helperText={errors.intake_date?.message || 'Día en que el cliente dejó el producto.'}
                {...register('intake_date')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller
                name="response_days"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label="Tiempo de respuesta"
                    error={Boolean(errors.response_days)}
                    helperText={errors.response_days?.message}
                  >
                    {GARANTIA_RESPONSE_DAYS_OPTIONS.map((days) => (
                      <MenuItem key={days} value={days}>
                        {days} días
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
          </Grid>

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Informe (descripción de la falla)"
            error={Boolean(errors.informe)}
            helperText={errors.informe?.message}
            {...register('informe')}
          />

          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Observaciones"
            placeholder="Ej. Sin caja, sin cable, con golpes en la carcasa..."
            error={Boolean(errors.observations)}
            helperText={errors.observations?.message}
            {...register('observations')}
          />

          {savedGarantia && (
            <>
              <Divider />
              <Stack spacing={1}>
                <Typography variant="subtitle1">Fotos (máximo 3)</Typography>
                <GarantiaImagesManager
                  garantiaId={savedGarantia.id}
                  images={savedGarantia.images || []}
                  onChanged={handleImagesChanged}
                />
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        <Button type="submit" form="garantia-form" variant="contained" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Registrar garantía'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

GarantiaFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  garantia: PropTypes.object,
};
